import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UserRole, useAuth } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { SystemService } from '../../../services/system.service';
import { BannersService } from '../../../services/banners.service';
import { useNotification } from '../../../context/NotificationContext';
import SidebarBannerCarousel from '../../common/banners/SidebarBannerCarousel';
import type { Banner } from '../../../types/Banner';
import { isSessionIdleSuppressed } from '../../../utils/sessionActivity';

import {
  FiHome,
  FiDollarSign,
  FiUsers,
  FiSettings,
  FiBriefcase,
  FiZap,
  FiUser,
  FiTool,
  FiBookOpen,
  FiPhone,
  FiPieChart,
  FiClipboard,
  FiCreditCard,
  FiArrowUpCircle,
  FiShield,
  FiChevronDown,
  FiFolder,
  FiGitBranch
} from 'react-icons/fi';

import styles from './Sidebar.module.css';

interface SidebarProps {
  userRole?: UserRole;
  isOpen: boolean;
  onLinkClick?: () => void;
}

type LinkItem = {
  key: string;
  path: string;
  Icon: React.ComponentType<any>;
  gate?: 'reports_enabled' | 'support_enabled';
  permission?: string;
};

interface AdminNavItem {
  key: string;
  path: string;
  icon: React.ComponentType<any>;
  permission?: string;
  gate?: 'reports_enabled' | 'support_enabled';
}

interface AdminNavSection {
  title: string;
  items: AdminNavItem[];
}

interface AdminNavMenu {
  ungrouped: AdminNavItem[];
  sections: AdminNavSection[];
}

const adminNavMenu: AdminNavMenu = {
  ungrouped: [
    { key: 'common.admin_dashboard', path: '/admin/dashboard', icon: FiHome, permission: 'nav.admin.dashboard' },
    { key: 'common.projects', path: '/admin/proyectos', icon: FiFolder, permission: 'nav.admin.projects' },
  ],
  sections: [
    {
      title: 'Finanzas',
      items: [
        { key: 'common.investments', path: '/admin/inversiones', icon: FiDollarSign, permission: 'nav.admin.investments' },
        { key: 'admin.withdrawals', path: '/admin/retiros', icon: FiArrowUpCircle, permission: 'nav.admin.withdrawals' },
        { key: 'admin.bank_accounts', path: '/admin/cuentas-bancarias', icon: FiCreditCard, permission: 'nav.admin.bank_accounts' },
        { key: 'admin.transfer_proofs', path: '/admin/comprobantes-transferencia', icon: FiCreditCard, permission: 'nav.admin.transfer_proofs' },
      ],
    },
    {
      title: 'Sistema',
      items: [
        { key: 'admin.approval_title', path: '/admin/aprobacion-usuarios', icon: FiUsers, permission: 'nav.admin.approval' },
        { key: 'admin.developer_requests', path: '/admin/solicitudes-desarrollador', icon: FiGitBranch, permission: 'nav.admin.developer_requests' },
        { key: 'admin.kyc_review', path: '/admin/kyc', icon: FiShield, permission: 'nav.admin.kyc_review' },
        { key: 'admin.document_types', path: '/admin/tipos-documentos', icon: FiClipboard, permission: 'nav.admin.document_types' },
        { key: 'admin.project_documents_review', path: '/admin/proyectos/documentos', icon: FiClipboard, permission: 'nav.admin.project_documents_review' },
        { key: 'audit.title', path: '/admin/bitacora', icon: FiClipboard, permission: 'nav.admin.audit' },
        { key: 'common.support', path: '/admin/soporte', icon: FiTool, permission: 'nav.admin.support' },
        { key: 'admin.system_config_title', path: '/admin/configuracion', icon: FiSettings, permission: 'nav.admin.system_config' },
        { key: 'admin.roles', path: '/admin/roles', icon: FiTool, permission: 'nav.admin.roles' },
        { key: 'common.profile', path: '/admin/perfil', icon: FiUser, permission: 'nav.admin.profile' },
      ],
    },
  ],
};

const Sidebar: React.FC<SidebarProps> = ({ userRole, isOpen, onLinkClick }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { addNotification } = useNotification();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const effectiveRole = userRole ?? user?.role ?? 'client';
  const normalizedRole = String(effectiveRole || 'client').toLowerCase();
  const permissionsProvided = Array.isArray(user?.permissions);
  const permissions = permissionsProvided ? (user?.permissions as string[]) : [];
  const shouldFilterByPermissions =
    permissionsProvided &&
    permissions.length > 0 &&
    (normalizedRole === 'admin' || normalizedRole === 'manager');

  const [reportsEnabled, setReportsEnabled] = useState(true);
  const [supportEnabled, setSupportEnabled] = useState(true);
  const [adminBanners, setAdminBanners] = useState<Banner[]>([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [r, s] = await Promise.all([
          SystemService.getFlag('reports_enabled'),
          SystemService.getFlag('support_enabled')
        ]);
        if (!alive) return;
        setReportsEnabled(!!r);
        setSupportEnabled(!!s);
      } catch {
        if (!alive) return;
        setReportsEnabled(true);
        setSupportEnabled(true);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const loadBanners = async () => {
      if (normalizedRole !== 'admin') return;
      try {
        const res = await BannersService.getAdminSidebarBanners();
        if (!alive) return;
        setAdminBanners(Array.isArray(res) ? res : []);
      } catch {
        if (!alive) return;
        setAdminBanners([]);
      }
    };
    loadBanners();
    return () => { alive = false; };
  }, [normalizedRole]);

  const adminLinks: LinkItem[] = useMemo(
    () => [
      { key: 'common.admin_dashboard', path: '/admin/dashboard', Icon: FiHome, permission: 'nav.admin.dashboard' },
      { key: 'common.projects', path: '/admin/proyectos', Icon: FiBriefcase, permission: 'nav.admin.projects' },
      { key: 'common.investments', path: '/admin/inversiones', Icon: FiDollarSign, permission: 'nav.admin.investments' },
      { key: 'admin.transfer_proofs', path: '/admin/comprobantes-transferencia', Icon: FiCreditCard, permission: 'nav.admin.transfer_proofs' },
      { key: 'admin.bank_accounts', path: '/admin/cuentas-bancarias', Icon: FiCreditCard, permission: 'nav.admin.bank_accounts' },
      { key: 'admin.kyc_review', path: '/admin/kyc', Icon: FiShield, permission: 'nav.admin.kyc_review' },
      { key: 'admin.approval_title', path: '/admin/aprobacion-usuarios', Icon: FiUsers, permission: 'nav.admin.approval' },
      { key: 'admin.developer_requests', path: '/admin/solicitudes-desarrollador', Icon: FiUsers, permission: 'nav.admin.developer_requests' },
      { key: 'admin.project_documents_review', path: '/admin/proyectos/documentos', Icon: FiClipboard, permission: 'nav.admin.project_documents_review' },
      { key: 'admin.document_types', path: '/admin/tipos-documentos', Icon: FiClipboard, permission: 'nav.admin.document_types' },
      { key: 'common.blogs', path: '/admin/blogs', Icon: FiBookOpen, permission: 'nav.admin.blogs' },
      { key: 'admin.withdrawals', path: '/admin/retiros', Icon: FiArrowUpCircle, permission: 'nav.admin.withdrawals' },
      { key: 'audit.title', path: '/admin/bitacora', Icon: FiClipboard, permission: 'nav.admin.audit' },
      { key: 'common.support', path: '/admin/soporte', Icon: FiSettings, gate: 'support_enabled', permission: 'nav.admin.support' },
      { key: 'admin.system_config_title', path: '/admin/configuracion', Icon: FiTool, permission: 'nav.admin.system_config' },
      { key: 'admin.roles', path: '/admin/roles', Icon: FiTool, permission: 'nav.admin.roles' },
      { key: 'common.profile', path: '/admin/perfil', Icon: FiUser, permission: 'nav.admin.profile' }
    ],
    []
  );

  const clientLinks: LinkItem[] = useMemo(
    () => [
      { key: 'common.dashboard', path: '/dashboard', Icon: FiHome, permission: 'nav.client.dashboard' },
      { key: 'common.projects', path: '/proyectos', Icon: FiBriefcase, permission: 'nav.client.projects' },
      { key: 'common.investments', path: '/mis-inversiones', Icon: FiDollarSign, permission: 'nav.client.investments' },
      { key: 'portfolio.title', path: '/portafolio', Icon: FiPieChart, permission: 'nav.client.portfolio' },
      { key: 'common.transfer_proofs', path: '/comprobantes-transferencia', Icon: FiCreditCard, permission: 'nav.client.transfer_proofs' },
      { key: 'common.withdrawals', path: '/retiros', Icon: FiArrowUpCircle, permission: 'nav.client.withdrawals' },
      { key: 'common.reports', path: '/reportes', Icon: FiZap, gate: 'reports_enabled', permission: 'nav.client.reports' },
      { key: 'common.blogs', path: '/blogs', Icon: FiBookOpen, permission: 'nav.client.blogs' },
      { key: 'contact.title', path: '/datos-de-contacto', Icon: FiPhone, permission: 'nav.client.contact' },
      { key: 'common.profile', path: '/perfil', Icon: FiUser, permission: 'nav.client.profile' }
    ],
    []
  );

  const developerLink: LinkItem = {
    key: 'developer.panel',
    path: '/developer/panel',
    Icon: FiBriefcase,
    permission: 'nav.developer.panel'
  };

  const managerLinks = useMemo(
    () =>
      adminLinks.filter(
        link =>
          link.path !== '/admin/inversiones' &&
          link.path !== '/admin/retiros' &&
          link.path !== '/admin/bitacora' &&
          link.path !== '/admin/soporte' &&
          link.path !== '/admin/configuracion' &&
          link.path !== '/admin/roles'
      ),
    [adminLinks]
  );

  const developerLinks = useMemo(
    () =>
      clientLinks.filter(link =>
        ['/proyectos', '/blogs', '/datos-de-contacto', '/perfil'].includes(link.path)
      ),
    [clientLinks]
  );

  const baseLinks =
    normalizedRole === 'admin'
      ? adminLinks
      : normalizedRole === 'manager'
        ? managerLinks
        : normalizedRole === 'developer'
          ? [developerLink, ...developerLinks]
          : normalizedRole === 'client'
            ? clientLinks
            : [...adminLinks, ...clientLinks, developerLink];

  const permissionFilteredLinks = useMemo(() => {
    if (!shouldFilterByPermissions) return baseLinks;
    return baseLinks.filter(link => {
      if (!link.permission) return true;
      if (permissions.includes(link.permission)) return true;
      if (normalizedRole === 'admin' && link.path === '/admin/kyc') return true;
      return false;
    });
  }, [baseLinks, normalizedRole, permissions, shouldFilterByPermissions]);

  const gatedLinks = useMemo(() => {
    return permissionFilteredLinks.filter(l => {
      if (!l.gate) return true;
      if (l.gate === 'reports_enabled') return reportsEnabled;
      if (l.gate === 'support_enabled') return supportEnabled;
      return true;
    });
  }, [permissionFilteredLinks, reportsEnabled, supportEnabled]);

  const handleNavClick = (event: React.MouseEvent) => {
    const last = Number(localStorage.getItem('last_activity_ts') || 0);
    const expired = last > 0 && Date.now() - last >= 3 * 60 * 1000;
    if (expired && !isSessionIdleSuppressed()) {
      event.preventDefault();
      event.stopPropagation();
      logout({ silent: true, reason: 'EXPIRED' });
      addNotification('Tu sesi�n ha expirado por inactividad. Por favor inicia sesi�n nuevamente.', 'warning');
      return;
    }
    onLinkClick?.();
  };

  const toggleSection = (sectionTitle: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionTitle)) {
      newExpanded.delete(sectionTitle);
    } else {
      newExpanded.add(sectionTitle);
    }
    setExpandedSections(newExpanded);
  };

  const isPathActive = (path: string, location: string): boolean => {
    return location.startsWith(path);
  };

  const shouldShowItem = (item: AdminNavItem): boolean => {
    if (item.permission) {
      if (shouldFilterByPermissions && !permissions.includes(item.permission)) {
        if (!(normalizedRole === 'admin' && item.path === '/admin/kyc')) {
          return false;
        }
      }
    }
    if (item.gate) {
      if (item.gate === 'support_enabled' && !supportEnabled) return false;
      if (item.gate === 'reports_enabled' && !reportsEnabled) return false;
    }
    return true;
  };

  const currentLocation = window.location.pathname;

  return (
    <>
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={onLinkClick}
          aria-hidden="true"
        />
      )}
      <aside className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}>
        <div className={styles.logoArea}>
          <img src="/Profunding_Azul.png" alt="ProFunding" className={styles.sidebarLogo} />
        </div>

        <div className={styles.navContent}>
          <ul className={styles.menuList}>
            {normalizedRole === 'admin' ? (
              <>
                {adminNavMenu.ungrouped
                  .filter(shouldShowItem)
                  .map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.path} className={styles.menuItem}>
                        <NavLink
                          to={item.path}
                          onClick={handleNavClick}
                          className={({ isActive: linkActive }) => `${styles.link} ${linkActive ? styles.active : ''}`}
                          end={item.path === '/admin/dashboard' || item.path === '/admin/proyectos'}
                        >
                          <Icon className={styles.icon} />
                          <span className={styles.label}>{t(item.key)}</span>
                        </NavLink>
                      </li>
                    );
                  })}

                {adminNavMenu.sections.map(section => {
                  const filteredItems = section.items.filter(shouldShowItem);
                  if (filteredItems.length === 0) return null;
                  const isExpanded = expandedSections.has(section.title);
                  const isSectionActive_flag = filteredItems.some(item => isPathActive(item.path, currentLocation));

                  return (
                    <li key={section.title} className={styles.menuSection}>
                      <button
                        onClick={() => toggleSection(section.title)}
                        className={`${styles.sectionButton} ${isExpanded ? styles.sectionExpanded : ''} ${isSectionActive_flag ? styles.sectionActive : ''}`}
                      >
                        <FiChevronDown className={styles.chevron} />
                        <span className={styles.label}>{section.title}</span>
                      </button>
                      <ul className={`${styles.submenuList} ${isExpanded ? styles.submenuOpen : ''}`}>
                        {filteredItems.map(item => {
                          const Icon = item.icon;
                          return (
                            <li key={item.path} className={styles.submenuItem}>
                              <NavLink
                                to={item.path}
                                onClick={handleNavClick}
                                className={({ isActive: linkActive }) => `${styles.submenuLink} ${linkActive ? styles.active : ''}`}
                              >
                                <Icon className={styles.icon} />
                                <span className={styles.label}>{t(item.key)}</span>
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </>
            ) : (
              gatedLinks.map(link => (
                <li key={link.path} className={styles.menuItem}>
                  <NavLink
                    to={link.path}
                    onClick={handleNavClick}
                    className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
                    end={
                      link.path === '/dashboard' ||
                      link.path === '/admin/dashboard' ||
                      link.path === '/blogs' ||
                      link.path === '/admin/blogs' ||
                      link.path === '/datos-de-contacto' ||
                      link.path === '/admin/datos-de-contacto' ||
                      link.path === '/admin/proyectos' ||
                      link.path === '/admin/inversiones' ||
                      link.path === '/admin/cuentas-bancarias' ||
                      link.path === '/portafolio' ||
                      link.path === '/admin/bitacora'
                    }
                  >
                    <link.Icon className={styles.icon} />
                    <span className={styles.label}>{t(link.key)}</span>
                  </NavLink>
                </li>
              ))
            )}
          </ul>

          {normalizedRole === 'admin' && adminBanners.length > 0 ? (
            <div className={styles.bannerArea}>
              <SidebarBannerCarousel banners={adminBanners} height={140} />
            </div>
          ) : null}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
